import { Component, type ReactNode } from 'react';
import { ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { Button } from '../ui/Button';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary Component
 *
 * Catches React errors in child components and shows a fallback UI
 * instead of crashing the entire app.
 *
 * Usage:
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console
    console.error('Error Boundary caught an error:', error, errorInfo);

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // TODO: Log to Firebase Analytics when available
    // logEvent(analytics, 'error', {
    //   error_message: error.message,
    //   error_stack: error.stack,
    //   component_stack: errorInfo.componentStack,
    // });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen bg-surface-50 dark:bg-surface-900 flex items-center justify-center px-4">
          <div className="max-w-md w-full">
            <div className="bg-white dark:bg-surface-800 rounded-xl shadow-lg border border-surface-200 dark:border-surface-700 p-8 text-center">
              {/* Icon */}
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-danger-100 dark:bg-danger-900/30 flex items-center justify-center">
                <ExclamationTriangleIcon className="w-8 h-8 text-danger-600 dark:text-danger-400" />
              </div>

              {/* Title */}
              <h1 className="font-display text-2xl font-bold text-navy-800 dark:text-gold-500 mb-3">
                Something Went Wrong
              </h1>

              {/* Description */}
              <p className="text-surface-600 dark:text-surface-400 mb-6">
                We encountered an unexpected error. Don't worry - your data is safe.
                Try refreshing the page or going back to the dashboard.
              </p>

              {/* Error details (dev mode only) */}
              {import.meta.env.DEV && this.state.error && (
                <div className="mb-6 p-4 bg-surface-100 dark:bg-surface-900 rounded-lg text-left">
                  <p className="text-xs font-mono text-danger-600 dark:text-danger-400 break-all">
                    {this.state.error.message}
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="space-y-3">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={this.handleReload}
                  leftIcon={<ArrowPathIcon className="w-5 h-5" />}
                  fullWidth
                >
                  Reload Page
                </Button>
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={this.handleReset}
                  fullWidth
                >
                  Try Again
                </Button>
                <a
                  href="/dashboard"
                  className="block text-sm text-teal-600 dark:text-teal-400 hover:underline"
                >
                  Go to Dashboard
                </a>
              </div>

              {/* Support info */}
              <p className="mt-6 text-xs text-surface-500 dark:text-surface-400">
                If this keeps happening, please{' '}
                <a
                  href="https://github.com/mhgeddie/daily-reprieve-claude/issues"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-teal-600 dark:text-teal-400 hover:underline"
                >
                  report this issue
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
